import React, { useState, useCallback } from 'react';
import { IssueItem } from '../../models/issueItem';
import FileUploader from '../common/FileUploader'; // Import the reusable component
import issueService from '../../services/issueService';
// Assuming you might have specific styles for the loader context
// import './IssuesLoader.css';

interface IssuesLoaderProps {
  linkPrefix: string;
  onIssuesLoaded: (file: File, issues: IssueItem[]) => void;
  // Optional: Pass down a class name for specific styling
  className?: string;
}

const IssuesLoader: React.FC<IssuesLoaderProps> = ({ linkPrefix, onIssuesLoaded, className = '' }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Callback for the FileUploader component
  const handleFileSelected = useCallback((file: File) => {
    setError(null);
    setIsLoading(true);

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        if (!content) {
          throw new Error("File content is empty or unreadable.");
        }

        let loadedIssues: IssueItem[] = [];

        // Determine file type and parse accordingly
        if (file.name.toLowerCase().endsWith('.csv')) {
          loadedIssues = issueService.getIssuesFromCSV( content, linkPrefix);
        } else if (file.name.toLowerCase().endsWith('.json')) {
          loadedIssues = issueService.getIssuesFromJSON(content, linkPrefix);
        } else {
          throw new Error("Unsupported file type. Please upload JSON or CSV.");
        }

        // Add index if missing (assuming it should be sequential based on load order)
        loadedIssues = loadedIssues.map((issue, index) => ({
            ...issue,
            index: issue.index ?? index + 1, // Assign index if not present
        }));

        onIssuesLoaded( file, loadedIssues);
        setError(null); // Clear error on success
      } catch (err: any) {
        console.error("Error processing file:", err);
        setError(`Error processing file: ${err.message}`);
        onIssuesLoaded( file, []); // Clear issues on error
      } finally {
        setIsLoading(false);
      }
    };

    reader.onerror = () => {
      setError(`Error reading file: ${reader.error?.message || 'Unknown error'}`);
      setIsLoading(false);
      onIssuesLoaded( file, []); // Clear issues on error
    };

    // Read the file as text
    reader.readAsText(file);

  }, [ onIssuesLoaded, linkPrefix]); // Dependency array

  return (
    // Use the passed className for context-specific styling
    <div className={`issues-loader-container ${className}`}>
      <h4>Load Issues from File</h4>
      <FileUploader
        onFileSelected={handleFileSelected}
        // Specify accepted file types for issues
        acceptedFileTypes=".json,.csv"
        label="Drag & Drop Issues File (JSON or CSV)"
        buttonText="Select Issues File"
        // Optional: Add a specific class for styling within this context
        // className="issues-file-uploader"
      />
      {isLoading && <p>Loading...</p>}
      {error && <p style={{ color: 'var(--error-color, orange)', marginTop: '0.5rem' }}>{error}</p>}
    </div>
  );
};

export default IssuesLoader;
